<template>
    <div ref="container" :class="['drag-and-drop', disabled && 'disabled']">
        <ul ref="items1" class="styleless item-container">
            <li
                v-for="item in items1"
                :key="item.key"
                :class="['item', item.disabled && 'disabled']"
                @pointermove="evt => moveHandler(evt, item.key)"
                @touchmove="evt => moveHandler(evt, item.key)"
            >
                <img class="full-width block" :src="item.image">
                <div class="label">
                    {{ item.title }}
                </div>
            </li>
        </ul>
        <div ref="wrapper" class="drag-area-wrapper">
            <h2 class="text-center">
                {{ title }}
            </h2>
            <img
                ref="image"
                class="full-width block"
                :src="dropAreaImage"
            >
        </div>
        <ul ref="items2" class="styleless item-container">
            <li
                v-for="item in items2"
                :key="item.key"
                :class="['item', item.disabled && 'disabled']"
                @pointermove="evt => moveHandler(evt, item.key)"
                @touchmove="evt => moveHandler(evt, item.key)"
            >
                <img class="full-width block" :src="item.image">
                <div class="label">
                    {{ item.title }}
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import cloneDeep from 'lodash/cloneDeep';
import responder from '@kids/lib/service/responder';
import SlideState from '@/mixins/SlideState';
import dragItems from '@/static/modules/2.1/dragItems.json';
import userAgent from '@kids/lib/service/userAgent';

const {document, PointerEvent} = globalThis;

dragItems.forEach((item) =>
{
    item.image = require(`@/assets/modules/2.1/dd-${item.key}.png`);
    item.disabled = false;
});

// iPad event pressure is 0, use mousedown, mousemove and touchmove instead, quickfix
const invalidPressureValue = (userAgent.mobile.ios) ? 1 : 0;

export default {
    name: 'DragAndDrop',
    mixins: [SlideState],
    props: {
        title: {
            type: String,
            required: true
        },
        dropAreaImage: {
            type: String,
            required: true
        }
    },
    data: () => ({
        dragItems: cloneDeep(dragItems),
        disabled: false
    }),
    computed: {
        items1()
        {
            return this.dragItems.slice(0, 10);
        },
        items2()
        {
            return this.dragItems.slice(10);
        }
    },
    mounted()
    {
        const {$refs} = this;
        this._draggables = [];
        this._itemContainer = document.querySelector('.app');

        const state = this.getState();
        if (state)
        {
            this.$emit('drop', state.length);
            this.disabled = true;
        }

        this._timeline = gsap.timeline()
            .fromTo(
                $refs.items1.children,
                {rotate: 20, scale: 0.6},
                {duration: 0.3, opacity: 1, rotate: 0, scale: 1, stagger: 0.08}
            )
            .to($refs.wrapper, {duration: 0.6, opacity: 1}, '-=0.2')
            .fromTo(
                $refs.items2.children,
                {rotate: -20, scale: 0.6},
                {duration: 0.3, opacity: 1, rotate: 0, scale: 1, stagger: 0.08},
                '-=0.2'
            )
            .call(this.restore);

        responder.on('resize', this.resize);
    },
    destroyed()
    {
        this._draggables.slice(0).forEach(draggable => this.removeDraggable(draggable));
        this._timeline.kill();
        responder.off('resize', this.resize);
    },
    methods: {
        moveHandler(evt, key)
        {
            if (
                this._draggable ||
                (
                    PointerEvent &&
                    (evt.type === 'touchmove' || evt.pressure === invalidPressureValue)
                )
            )
            {
                return;
            }

            const config = this.getItemConfigByKey(key);
            if (config.disabled) { return; }

            let {pageX, pageY} = evt;
            if (evt.changedTouches)
            {
                ([{pageX, pageY}] = evt.changedTouches);
            }

            this._draggable = this.createDragImage(key, pageX, pageY);
            this.getItemConfigByKey(key).disabled = true;

            this._draggable.startDrag(evt);
        },
        dragStart(evt)
        {
            this._draggable = this._draggable || Draggable.get(evt.target);
        },
        dragEnd()
        {
            const draggable = this._draggable;
            if (!draggable) { return; }
            if (!draggable.hitTest(this.$refs.image, '100%'))
            {
                const {key} = draggable.vars;
                this.removeDraggable(draggable);
                this.getItemConfigByKey(key).disabled = false;
            }
            const state = this.createState();
            this.$emit('drop', state.length);
            this.saveState(state);
            this._draggable = null;
            this.$audioController.play('drop', {volume: 0.5});
        },
        removeDraggable(draggable)
        {
            const index = this._draggables.findIndex(d => d === draggable);
            if (index === -1) { return; }
            this._draggables.splice(index, 1);
            draggable.kill();
            const {target} = draggable;
            gsap.to(
                target,
                {opacity: 0, onComplete: () => { target.remove(); }}
            );
        },
        createState()
        {
            const {width, height} = this.getDimensions();

            return this._draggables.map((draggable) =>
            {
                const x = gsap.getProperty(draggable.target, 'x');
                const y = gsap.getProperty(draggable.target, 'y');

                return {
                    key: draggable.vars.key,
                    x: x / width,
                    y: y / height
                };
            });
        },
        getItemConfigByKey(key)
        {
            return this.dragItems.find(item => item.key === key);
        },
        restore()
        {
            if (!this.hasState()) { return; }

            const {width, height} = this.getDimensions();
            const state = this.getState();

            state.forEach((item) =>
            {
                const {key} = item;
                this.getItemConfigByKey(key).disabled = true;
                this.createDragImage(
                    key,
                    width * item.x,
                    height * item.y,
                    0.6
                );
            });
            this.disabled = false;
        },
        createDragImage(key, x, y, duration = 0.2)
        {
            const image = document.createElement('img');
            const itemConfig = this.getItemConfigByKey(key);

            image.setAttribute('src', itemConfig.image);
            image.classList.add('drag-image');
            this._itemContainer.appendChild(image);

            gsap.set(image, {x, y});
            gsap.to(image, {opacity: 1, duration});

            const [draggable] = Draggable.create(
                image,
                {
                    type: 'x,y',
                    onDragEnd: this.dragEnd,
                    onDragStart: this.dragStart,
                    bounds: '.slide-wrapper',
                    key: itemConfig.key
                }
            );
            this._draggables.push(draggable);

            return draggable;
        },
        resize()
        {
            if (!this.hasState()) { return; }
            const state = this.getState();
            const {width, height} = this.getDimensions();

            this._draggables.forEach((draggable, index) =>
            {
                const item = state[index];
                if (!item) { return; }
                gsap.set(
                    draggable.target,
                    {x: width * item.x, y: height * item.y}
                );
                draggable.update();
            });
        },
        getDimensions()
        {
            const element = this.$el;
            return {
                width: document.body.clientWidth,
                height: element.clientHeight + element.getBoundingClientRect().top +
                    globalThis.scrollY
            };
        }
    }
};
</script>

<style lang="scss" src="./DragAndDrop.scss" />

